import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, Box, FormGroup, FormControlLabel } from '@mui/material';
import { api } from '../api';
// --- THIS IS THE FIX ---
// We now use a named import to match the export style of the dialog component.
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';

const PermissionsPage = ({ state, users, refreshData }) => {
    const [userList, setUserList] = useState(users);
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'user' });

    const [keywordDialogOpen, setKeywordDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [assignedKeywords, setAssignedKeywords] = useState(new Set());
    
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);

    useEffect(() => {
        setUserList(users);
    }, [users]);
    
    const handlePermissionChange = async (userId, page, hasAccess) => {
        try {
            await api.put(`/users/${userId}/permissions`, { userId, page, hasAccess });
            const updatedUsers = userList.map(u => {
                if (u.id === userId) {
                    const existingPermissions = u.UserPermissions || [];
                    const otherPermissions = existingPermissions.filter(p => p.page !== page);
                    const updatedPermissions = [...otherPermissions, { page, hasAccess }];
                    return { ...u, UserPermissions: updatedPermissions };
                }
                return u;
            });
            setUserList(updatedUsers);
        } catch (error) {
            alert('Failed to update permission.');
            refreshData(); 
        }
    };
    
    const handleNewUserChange = (e) => {
        setNewUser(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleCreateUser = async () => {
        if (!newUser.email || !newUser.password) {
            return alert('Email and password are required.');
        }
        try {
            await api.post('/users', newUser);
            setAddUserDialogOpen(false);
            setNewUser({ email: '', password: '', displayName: '', role: 'user' });
            alert('User created successfully!');
            await refreshData();
        } catch (error) {
            alert('Failed to create user. Email may already exist.');
        }
    };

    const handleOpenKeywordDialog = (user) => {
        setSelectedUser(user);
        const userKeywordIds = new Set((user.Keywords || []).map(kw => kw.id));
        setAssignedKeywords(userKeywordIds);
        setKeywordDialogOpen(true);
    };

    const handleKeywordAssignmentChange = (keywordId) => {
        setAssignedKeywords(prev => {
            const newSet = new Set(prev);
            if (newSet.has(keywordId)) {
                newSet.delete(keywordId);
            } else {
                newSet.add(keywordId);
            }
            return newSet;
        });
    };

    const handleSaveKeywordAssignments = async () => {
        if (!selectedUser) return;
        try {
            await api.put(`/users/${selectedUser.id}/keywords`, { keywordIds: Array.from(assignedKeywords) });
            setKeywordDialogOpen(false);
            alert('Keyword assignments updated successfully!');
            await refreshData();
        } catch (error) {
            alert('Failed to update keyword assignments.');
        }
    };
    
    const handleOpenPasswordDialog = (user) => {
        setUserToEdit(user);
        setPasswordDialogOpen(true);
    };
    
    const pageNames = ['Dashboard', 'Insights', 'Keywords', 'Mentions', 'Sentiment', 'Trends', 'News', 'Facebook Monitoring', 'Raw Data View', 'Alerts', 'Simulation', 'Configuration','Permissions'];

    const hasAccess = (user, page) => {
        if (user.role === 'admin') return true;
        const permissions = user.UserPermissions || [];
        const permission = permissions.find(p => p.page === page);
        return permission ? permission.hasAccess : true;
    };
    
    return (
        <Container maxWidth="xl">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" gutterBottom>User & Permissions Management</Typography>
                <Button variant="contained" onClick={() => setAddUserDialogOpen(true)}>Add New User</Button>
            </Box>
            <Card>
                <CardContent>
                    <TableContainer component={Paper}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                                    {pageNames.map(page => <TableCell key={page} align="center" sx={{ fontWeight: 'bold', minWidth: '120px' }}>{page}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(userList || []).map(user => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.email} ({user.role})</TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Button size="small" onClick={() => handleOpenKeywordDialog(user)} disabled={user.role === 'admin'}>Keywords</Button>
                                                <Button size="small" onClick={() => handleOpenPasswordDialog(user)}>Password</Button>
                                            </Stack>
                                        </TableCell>
                                        {pageNames.map(page => (
                                            <TableCell key={page} align="center">
                                                <Checkbox
                                                    checked={hasAccess(user, page)}
                                                    onChange={(e) => handlePermissionChange(user.id, page, e.target.checked)}
                                                    disabled={user.role === 'admin'}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)}>
                <DialogTitle>Create New User</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1, minWidth: '400px' }}>
                        <TextField name="email" label="Email" type="email" value={newUser.email} onChange={handleNewUserChange} />
                        <TextField name="password" label="Password" type="password" value={newUser.password} onChange={handleNewUserChange} />
                        <TextField name="displayName" label="Display Name (Optional)" value={newUser.displayName} onChange={handleNewUserChange} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddUserDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateUser} variant="contained">Create User</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={keywordDialogOpen} onClose={() => setKeywordDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Assign Keywords for {selectedUser?.email}</DialogTitle>
                <DialogContent>
                    <FormGroup sx={{ mt: 1 }}>
                        {(state.keywords || []).map(kw => (
                            <FormControlLabel
                                key={kw.id}
                                control={<Checkbox
                                    checked={assignedKeywords.has(kw.id)}
                                    onChange={() => handleKeywordAssignmentChange(kw.id)}
                                />}
                                label={kw.term}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKeywordDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveKeywordAssignments} variant="contained">Save Assignments</Button>
                </DialogActions>
            </Dialog>

            {userToEdit && (
                <ChangePasswordDialog
                    open={passwordDialogOpen}
                    onClose={() => setPasswordDialogOpen(false)}
                    userToEdit={userToEdit}
                    isAdminMode={true}
                />
            )}
        </Container>
    );
};

export default PermissionsPage;

